import { Button } from "@/components/ui/UIElements/Button";
import React, { useEffect, useMemo, useState } from "react";
import { NativeSelect } from "@/components/ui/UIElements/Select";
import { Icon, Switch } from "../";
import { PhoneInputSimple } from "../UIElements/PhoneInput";
import { TIMEZONES } from "@finitless/shared/constants";

const GeneralTab = ({
  formData,
  handleInputChange,
  isEditing,
  updateMutation,
  handleLogoChange,
  logoInputKey,
  phoneInputKey,
  brand,
  handleRemoveLogo,
}) => {
  const isDisabled = !isEditing || updateMutation.isPending;

  const initialCellphoneFullNumber = useMemo(() => {
    const countryPhoneCode = String(formData.country_phone_code || "").replace(
      /\D/g,
      "",
    );
    const cellphone = String(formData.cellphone || "")
      .replace(/\D/g, "")
      .slice(0, 15);
    if (!countryPhoneCode || !cellphone) return "";
    return `+${countryPhoneCode}${cellphone}`;
  }, [formData.country_phone_code, formData.cellphone]);

  const [cellphoneFullNumber, setCellphoneFullNumber] = useState(
    initialCellphoneFullNumber,
  );

  useEffect(() => {
    setCellphoneFullNumber(initialCellphoneFullNumber);
  }, [initialCellphoneFullNumber]);

  const handlePhoneDigitsChange = (e) => {
    const { name, value } = e.target;
    const numericValue = String(value || "")
      .replace(/\D/g, "")
      .slice(0, 15);
    handleInputChange({ target: { name, value: numericValue } });
  };

  const handleCellphoneFullNumberChange = (value) => {
    const nextValue = value || "";
    setCellphoneFullNumber(nextValue);

    if (!nextValue) {
      handleInputChange({ target: { name: "country_phone_code", value: "" } });
      handleInputChange({ target: { name: "cellphone", value: "" } });
    }
  };

  const handleCellphoneDataChange = (data) => {
    const countryPhoneCode = String(data?.countryCode || "").replace(/\D/g, "");
    const cellphone = String(data?.phoneNumber || "")
      .replace(/\D/g, "")
      .slice(0, 15);

    handleInputChange({
      target: { name: "country_phone_code", value: countryPhoneCode },
    });
    handleInputChange({ target: { name: "cellphone", value: cellphone } });
  };

  return (
    <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
      {/* Left Side - Logo Only */}
      <div>
        <label
          id="brand-logo-label"
          className="mb-2 block text-center text-sm font-medium text-white"
        >
          Logo
        </label>
        <p className="text-muted-foreground mb-3 text-center text-xs">
          Accepted formats: PNG, JPG, JPEG (Max 2MB)
        </p>
        <div className="flex flex-col items-center justify-center">
          <label
            className={`cursor-pointer ${
              !isEditing ? "pointer-events-none" : ""
            }`}
          >
            <div className="border-border bg-card relative flex h-72 w-72 items-center justify-center overflow-hidden rounded-lg border">
              {formData.logo &&
              formData.logo !== null &&
              formData.logo !== "" ? (
                <>
                  <img
                    src={formData.logo.base64}
                    alt="Preview"
                    className="h-full w-full object-contain"
                  />
                  {/* Overlay - Dots and Image Icon - Only when editing */}
                  {isEditing && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                      <div className="flex items-center space-x-1">
                        <Icon name="Image" className="h-6 w-6 text-white" />
                        <Icon
                          name="MoreVertical"
                          className="h-6 w-6 text-white"
                        />
                      </div>
                    </div>
                  )}
                  {/* Clickable overlay - Always present when editing */}
                  {isEditing && (
                    <input
                      key={logoInputKey}
                      type="file"
                      aria-labelledby="brand-logo-label"
                      accept="image/png,image/jpg,image/jpeg"
                      onChange={handleLogoChange}
                      className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
                      disabled={updateMutation.isPending || !isEditing}
                    />
                  )}
                </>
              ) : formData.logo === "" ? null : brand.logo ? (
                <>
                  <img
                    src={brand.logo}
                    alt={brand.name}
                    className="h-full w-full object-contain"
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "flex";
                    }}
                  />
                  {/* Overlay - Dots and Image Icon - Only when editing */}
                  {isEditing && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                      <div className="flex items-center space-x-1">
                        <Icon name="Image" className="h-6 w-6 text-white" />
                        <Icon
                          name="MoreVertical"
                          className="h-6 w-6 text-white"
                        />
                      </div>
                    </div>
                  )}
                  {/* Clickable overlay - Always present when editing */}
                  {isEditing && (
                    <input
                      key={logoInputKey}
                      type="file"
                      aria-labelledby="brand-logo-label"
                      accept="image/png,image/jpg,image/jpeg"
                      onChange={handleLogoChange}
                      className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
                      disabled={updateMutation.isPending || !isEditing}
                    />
                  )}
                </>
              ) : null}
              {/* Default placeholder when no logo */}
              {(!formData.logo || formData.logo === "") && !brand.logo && (
                <>
                  <div className="absolute inset-0 flex h-full w-full items-center justify-center">
                    <Icon
                      name="Building"
                      className="text-muted-foreground h-20 w-20"
                    />
                  </div>
                  {/* Overlay for upload when editing */}
                  {isEditing && (
                    <>
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                        <div className="flex items-center space-x-1">
                          <Icon name="Image" className="h-6 w-6 text-white" />
                          <Icon
                            name="MoreVertical"
                            className="h-6 w-6 text-white"
                          />
                        </div>
                      </div>
                      <input
                        key={logoInputKey}
                        type="file"
                        aria-labelledby="brand-logo-label"
                        accept="image/png,image/jpg,image/jpeg"
                        onChange={handleLogoChange}
                        className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
                        disabled={updateMutation.isPending || !isEditing}
                      />
                    </>
                  )}
                </>
              )}
            </div>
          </label>
          {/* Remove Logo Button - Below the logo input */}
          {isEditing &&
            ((brand.logo && formData.logo !== "") ||
              (formData.logo && formData.logo !== "")) && (
              <div className="mt-3 flex justify-center">
                <Button
                  variant="ghost"
                  type="button"
                  onClick={handleRemoveLogo}
                  className="bg-destructive hover:bg-destructive/90 flex items-center space-x-1 rounded-lg px-3 py-1.5 text-xs font-medium text-white shadow transition-colors"
                >
                  <Icon name="Trash2" className="h-3 w-3" />
                  <span>Remove Logo</span>
                </Button>
              </div>
            )}
        </div>
      </div>

      {/* Right Side - Status and Form Fields */}
      <div className="space-y-6">
        {/* Status */}
        <div>
          <label
            htmlFor="enabled"
            className="mb-2 block text-sm font-medium text-white"
          >
            Status
          </label>
          <div className="border-border bg-card flex h-11 items-center rounded-lg border px-3">
            <Switch
              id="enabled"
              checked={formData.enabled}
              onCheckedChange={(checked) =>
                handleInputChange({
                  target: { name: "enabled", value: checked },
                })
              }
              disabled={isDisabled}
            />
            <span className="text-foreground ml-3">
              {formData.enabled ? "Active" : "Inactive"}
            </span>
          </div>
        </div>

        {/* Name */}
        <div>
          <label
            htmlFor="brand-name"
            className="mb-2 block text-sm font-medium text-white"
          >
            Name
          </label>
          <input
            id="brand-name"
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            disabled={isDisabled}
            className="border-border bg-card focus:ring-ring h-11 w-full rounded-lg border px-3 text-white placeholder-gray-500 focus:ring-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>

        {/* Internal Notes */}
        <div>
          <label
            htmlFor="brand-internal-name"
            className="mb-2 block text-sm font-medium text-white"
          >
            Internal Notes
          </label>
          <input
            id="brand-internal-name"
            type="text"
            name="internal_name"
            value={formData.internal_name}
            onChange={handleInputChange}
            disabled={isDisabled}
            className="border-border bg-card focus:ring-ring h-11 w-full rounded-lg border px-3 text-white placeholder-gray-500 focus:ring-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            placeholder="Internal notes"
          />
        </div>

        {/* Cellphone */}
        <div>
          <label
            id="brand-cellphone-label"
            className="mb-2 block text-sm font-medium text-white"
          >
            Cellphone
          </label>
          <div
            role="group"
            aria-labelledby="brand-cellphone-label"
            className={`${
              isDisabled
                ? "pointer-events-none cursor-not-allowed opacity-50"
                : ""
            }`}
          >
            <PhoneInputSimple
              key={phoneInputKey}
              value={cellphoneFullNumber}
              onChange={handleCellphoneFullNumberChange}
              onPhoneDataChange={handleCellphoneDataChange}
              placeholder="Enter cellphone number"
              disabled={isDisabled}
              size="lg"
            />
          </div>
          <p className="text-muted-foreground mt-2 text-xs">
            Enter number without country code (max 15 digits). Country code is
            selected from the dropdown.
          </p>
        </div>

        {/* Phone */}
        <div>
          <label
            htmlFor="brand-phone"
            className="mb-2 block text-sm font-medium text-white"
          >
            Phone
          </label>
          <input
            id="brand-phone"
            type="tel"
            inputMode="numeric"
            pattern="\d*"
            name="phone"
            value={formData.phone || ""}
            onChange={handlePhoneDigitsChange}
            disabled={isDisabled}
            placeholder="Enter phone number (numbers only)"
            className="border-border bg-card focus:ring-ring h-11 w-full rounded-lg border px-3 text-white placeholder-gray-500 focus:ring-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          />
          <p className="text-muted-foreground mt-2 text-xs">
            Numbers only, max 15 digits (without country code).
          </p>
        </div>

        {/* Timezone */}
        <div>
          <label
            htmlFor="brand-timezone"
            className="mb-2 block text-sm font-medium text-white"
          >
            Timezone
          </label>
          <div className="relative">
            <NativeSelect
              id="brand-timezone"
              name="timezone"
              value={formData.timezone ?? brand?.timezone ?? ""}
              onChange={(e) =>
                handleInputChange({
                  target: { name: "timezone", value: e.target.value },
                })
              }
              disabled={!isEditing || updateMutation.isPending}
              className="h-11 w-full"
              aria-label="Select brand timezone"
            >
              {(TIMEZONES || []).map((timezone) => (
                <option key={timezone} value={timezone}>
                  {timezone}
                </option>
              ))}
            </NativeSelect>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneralTab;
